"use client";

import { useState } from "react";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Pencil, Trash2, Plus } from "lucide-react";
import { PricingService } from "@/lib/pricing/pricing-service-unified";
import { getCurrencySymbol } from "@/lib/utils/currency";
import { PRICE_TYPE_IDS } from "@/lib/constants/price-types";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

interface AddOn {
  id: number;
  add_on_id: number;
  quantity: number;
  amount: number;
  total_price: number;
  total_price_excl_vat?: number;
  total_price_incl_vat?: number;
  per_person_mode_price_excluding_vat?: number;
  fixed_mode_price_excluding_vat?: number;
  price_type_id?: number; // 1 = fixed, 2 = per_person
  add_ons: {
    id: number;
    pricing_id: number;
    status_id: number;
    add_ons_info: Array<{
      name: string;
      desc?: string;
      lang: string;
    }>;
    pricings?: {
      id: number;
      price: number;
      vat_percentage?: number;
    };
  };
}

interface AddOnsCardProps {
  bookingId: string;
  venueId: string;
  addOns: AddOn[];
  currency?: string;
  customerTypeId?: number;
  onUpdate: () => void;
  hideActions?: boolean;
  canEdit?: boolean;
}

export function AddOnsCard({
  bookingId,
  venueId,
  addOns,
  currency = "SEK",
  customerTypeId,
  onUpdate,
  hideActions = false,
  canEdit = false,
}: AddOnsCardProps) {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedAddOn, setSelectedAddOn] = useState<string>("");
  const [quantity, setQuantity] = useState<string>("1");
  const [priceOverride, setPriceOverride] = useState<string>("");
  const [sendEmail, setSendEmail] = useState(false);
  const [availableAddOns, setAvailableAddOns] = useState<any[]>([]);
  const [editingAddOn, setEditingAddOn] = useState<AddOn | null>(null);
  const [deletingAddOn, setDeletingAddOn] = useState<AddOn | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Fetch available add-ons when modal opens
  const handleOpenAddModal = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`/api/venues/${venueId}/add-ons`);
      if (!response.ok) {
        throw new Error(`Failed to load add-ons: ${response.statusText}`);
      }
      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        throw new Error("Server returned non-JSON response");
      }
      const data = await response.json();
      if (Array.isArray(data)) {
        setAvailableAddOns(data);
      } else {
        toast.error("Invalid add-ons data format");
      }
    } catch (error) {
      toast.error("Failed to load add-ons");
    } finally {
      setIsLoading(false);
      setIsAddModalOpen(true);
    }
  };

  const handleAddAddOn = async () => {
    if (!selectedAddOn) {
      toast.error("Please select an add-on");
      return;
    }

    setIsLoading(true);
    try {
      // Include existing add-ons plus the new one
      const allAddOns = [
        ...addOns.map((addon) => ({
          addOnId: addon.add_on_id,
          quantity: addon.quantity,
          pricingId: addon.add_ons.pricing_id,
          price: addon.price_type_id === PRICE_TYPE_IDS.PER_PERSON
            ? (addon as any).per_person_mode_price_excluding_vat || 0
            : (addon as any).fixed_mode_price_excluding_vat || 0,
          isPerPerson: addon.price_type_id === PRICE_TYPE_IDS.PER_PERSON,
        })),
        {
          addOnId: parseInt(selectedAddOn),
          quantity: parseInt(quantity) || 1,
          pricingId: selectedAddOnData?.pricing_id,
          price: priceOverride
            ? parseFloat(priceOverride)
            : selectedAddOnData?.pricings?.price || 0,
          isPerPerson:
            selectedAddOnData?.price_type_id === PRICE_TYPE_IDS.PER_PERSON,
        },
      ];

      const response = await fetch(
        `/api/staff/venues/${venueId}/bookings/${bookingId}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            addOns: allAddOns,
            changedFields: { isAddOnChange: true },
          }),
        },
      );

      if (!response.ok) {
        const contentType = response.headers.get("content-type");
        if (contentType && contentType.includes("application/json")) {
          const error = await response.json();
          throw new Error(
            error.error || `Failed to add add-on: ${response.statusText}`,
          );
        } else {
          throw new Error(
            `Failed to add add-on: ${response.status} ${response.statusText}`,
          );
        }
      }

      const data = await response.json();
      if (data.success) {
        toast.success("Add-on added successfully");
        setIsAddModalOpen(false);
        resetForm();
        onUpdate();
      } else {
        toast.error(data.error || "Failed to add add-on");
      }
    } catch (error) {
      toast.error("Failed to add add-on");
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditAddOn = (addOn: AddOn) => {
    setEditingAddOn(addOn);
    setQuantity(addOn.quantity.toString());
    setPriceOverride(addOn.amount.toString());
    setIsEditModalOpen(true);
  };

  const handleUpdateAddOn = async () => {
    if (!editingAddOn) return;

    setIsLoading(true);
    try {
      // Update the specific add-on and include all others
      const allAddOns = addOns.map((addon) => {
        if (addon.id === editingAddOn.id) {
          return {
            addOnId: addon.add_on_id,
            quantity: parseInt(quantity) || 1,
            pricingId: addon.add_ons.pricing_id,
            price: priceOverride ? parseFloat(priceOverride) : addon.amount,
            isPerPerson: addon.price_type_id === PRICE_TYPE_IDS.PER_PERSON,
          };
        } else {
          return {
            addOnId: addon.add_on_id,
            quantity: addon.quantity,
            pricingId: addon.add_ons.pricing_id,
            price: addon.price_type_id === PRICE_TYPE_IDS.PER_PERSON
            ? (addon as any).per_person_mode_price_excluding_vat || 0
            : (addon as any).fixed_mode_price_excluding_vat || 0,
            isPerPerson: addon.price_type_id === PRICE_TYPE_IDS.PER_PERSON,
          };
        }
      });

      const response = await fetch(
        `/api/staff/venues/${venueId}/bookings/${bookingId}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            addOns: allAddOns,
            changedFields: { isAddOnChange: true },
          }),
        },
      );

      if (!response.ok) {
        const contentType = response.headers.get("content-type");
        if (contentType && contentType.includes("application/json")) {
          const error = await response.json();
          throw new Error(
            error.error || `Failed to update add-on: ${response.statusText}`,
          );
        } else {
          throw new Error(
            `Failed to update add-on: ${response.status} ${response.statusText}`,
          );
        }
      }

      const data = await response.json();
      if (data.success) {
        toast.success("Add-on updated successfully");
        setIsEditModalOpen(false);
        resetForm();
        onUpdate();
      } else {
        toast.error(data.error || "Failed to update add-on");
      }
    } catch (error) {
      toast.error("Failed to update add-on");
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveAddOn = (addOn: AddOn) => {
    setDeletingAddOn(addOn);
    setIsDeleteModalOpen(true);
  };

  const confirmRemoveAddOn = async () => {
    if (!deletingAddOn) return;

    setIsLoading(true);
    try {
      // Get current add-ons excluding the one to delete
      const remainingAddOns = addOns
        .filter((addon) => addon.id !== deletingAddOn.id)
        .map((addon) => ({
          addOnId: addon.add_on_id,
          quantity: addon.quantity,
          pricingId: addon.add_ons.pricing_id,
          price: addon.price_type_id === PRICE_TYPE_IDS.PER_PERSON
            ? (addon as any).per_person_mode_price_excluding_vat || 0
            : (addon as any).fixed_mode_price_excluding_vat || 0,
          isPerPerson: addon.price_type_id === PRICE_TYPE_IDS.PER_PERSON,
        }));

      const response = await fetch(
        `/api/staff/venues/${venueId}/bookings/${bookingId}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            addOns: remainingAddOns,
            changedFields: { isAddOnChange: true },
          }),
        },
      );

      if (!response.ok) {
        const contentType = response.headers.get("content-type");
        if (contentType && contentType.includes("application/json")) {
          const error = await response.json();
          throw new Error(
            error.error || `Failed to remove add-on: ${response.statusText}`,
          );
        } else {
          throw new Error(
            `Failed to remove add-on: ${response.status} ${response.statusText}`,
          );
        }
      }

      const data = await response.json();
      if (data.success) {
        toast.success("Add-on removed successfully");
        setIsDeleteModalOpen(false);
        setDeletingAddOn(null);
        onUpdate();
      } else {
        toast.error(data.error || "Failed to remove add-on");
      }
    } catch (error) {
      toast.error("Failed to remove add-on");
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setSelectedAddOn("");
    setQuantity("1");
    setPriceOverride("");
    setSendEmail(false);
    setEditingAddOn(null);
  };

  const selectedAddOnData = availableAddOns.find(
    (a) => a.id === parseInt(selectedAddOn),
  );
  const originalPrice = selectedAddOnData?.pricings?.price || 0;
  const originalTotal = originalPrice * parseInt(quantity || "0");
  const newTotal = priceOverride
    ? parseFloat(priceOverride) * parseInt(quantity || "0")
    : originalTotal;
  const priceDifference = newTotal - originalTotal;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-lg font-semibold text-gray-900">
          Add-ons
        </CardTitle>
        {/* Show Add Addon button based on canEdit permission */}
        {canEdit && (
          <Link
            href={`/${typeof window !== 'undefined' ? window.location.pathname.split("/")[1] : 'superadmin'}/venue/${venueId}/booking-new?bookingId=${bookingId}&addon=yes`}
          >
            <Button
              className="bg-white border border-purple-600 text-purple-600 hover:bg-purple-50"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Addon
            </Button>
          </Link>
        )}
      </CardHeader>
      <CardContent>
        {addOns.length === 0 ? (
          <p className="text-muted-foreground text-center py-8">
            No add-ons added
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Add-on Name</TableHead>
                <TableHead>Quantity</TableHead>
                <TableHead>Unit Price</TableHead>
                <TableHead>Total</TableHead>
                <TableHead>VAT</TableHead>
                {/* <TableHead className="text-right">Actions</TableHead> */}
              </TableRow>
            </TableHeader>
            <TableBody>
              {addOns.map((addon) => {
                // Use PricingService for proper VAT calculations
                const vatPercentage =
                  addon.add_ons.pricings?.vat_percentage || 0;
                const isPrivateBooking = customerTypeId === 1;

                // Determine if this is a per-person or fixed add-on
                // For per-person: quantity represents participant count
                // For fixed: quantity represents number of units
                // price_type_id: 1 = fixed, 2 = per_person
                const isPerPerson = addon.price_type_id === 2;

                // Display quantity directly from booking_add_ons table
                const displayQuantity = addon.quantity || 1;

                // Get the correct unit price from the appropriate field
                // The amount field is not used, prices are stored in specific fields
                const unitPrice = isPerPerson
                  ? (addon as any).per_person_mode_price_excluding_vat || (addon as any).total_price_excl_vat / displayQuantity || 0
                  : (addon as any).fixed_mode_price_excluding_vat || (addon as any).total_price_excl_vat / displayQuantity || 0;

                // Calculate using PricingService
                const priceCalc = PricingService.calculate({
                  unitPrice: unitPrice, // Use the correct unit price
                  vatPercentage: vatPercentage,
                  participants: isPerPerson ? displayQuantity : 1,
                  customerType: isPrivateBooking ? "private" : "corporate",
                  priceType: isPerPerson ? "per_person" : "fixed",
                });

                // For fixed add-ons, multiply by quantity
                const totalBaseAmount = isPerPerson
                  ? priceCalc.baseAmount
                  : priceCalc.baseAmount * displayQuantity;
                const totalVatAmount = isPerPerson
                  ? priceCalc.vatAmount
                  : priceCalc.vatAmount * displayQuantity;

                // For corporate, calculate what VAT would be (for display purposes)
                const invoiceVatAmount =
                  customerTypeId === 2
                    ? totalBaseAmount * (vatPercentage / 100)
                    : totalVatAmount;

                return (
                  <TableRow key={addon.id}>
                    <TableCell>
                      <div className="font-medium line-clamp-1">
                        {addon.add_ons.add_ons_info?.[0]?.name ||
                          "Unnamed Add-on"}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {isPerPerson ? "Per person" : "Individual"}
                      </div>
                    </TableCell>
                    <TableCell>{displayQuantity}</TableCell>
                    <TableCell>
                      {unitPrice.toFixed(2)} {getCurrencySymbol(currency)}
                    </TableCell>
                    <TableCell>
                      {totalBaseAmount.toFixed(2)} {getCurrencySymbol(currency)}
                    </TableCell>
                    <TableCell>
                      {customerTypeId === 2 && vatPercentage > 0 ? (
                        <span>
                          <span>
                            {invoiceVatAmount.toFixed(2)} {getCurrencySymbol(currency)}
                          </span>
                          <span className="text-muted-foreground italic text-xs ml-1">
                            (Invoice)
                          </span>
                        </span>
                      ) : (
                        <span>
                          {invoiceVatAmount.toFixed(2)} {getCurrencySymbol(currency)}
                        </span>
                      )}
                    </TableCell>
                    {/* <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEditAddOn(addon)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveAddOn(addon)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell> */}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>

      {/* Add Add-on Modal */}
      <Dialog open={isAddModalOpen} onOpenChange={setIsAddModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Add-on</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="addon-select">Select Add-on</Label>
              <Select value={selectedAddOn} onValueChange={setSelectedAddOn}>
                <SelectTrigger id="addon-select">
                  <SelectValue placeholder="Select an add-on" />
                </SelectTrigger>
                <SelectContent>
                  {availableAddOns.map((addon) => (
                    <SelectItem key={addon.id} value={addon.id.toString()}>
                      {addon.name || addon.add_ons_info?.[0]?.name || "Unnamed"}{" "}
                      - {addon.pricings?.price || addon.current_price || 0}{" "}
                      {getCurrencySymbol(currency)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="quantity">Number of Units</Label>
              <Input
                id="quantity"
                type="number"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                min="1"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="price-override">Price Override (per unit)</Label>
              <Input
                id="price-override"
                type="number"
                value={priceOverride}
                onChange={(e) => setPriceOverride(e.target.value)}
                placeholder={originalPrice.toString()}
              />
            </div>

            {selectedAddOnData && (
              <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Original Price</span>
                  <span>
                    {originalTotal} {getCurrencySymbol(currency)}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>New Price</span>
                  <span>
                    {newTotal} {getCurrencySymbol(currency)}
                  </span>
                </div>
                {priceDifference !== 0 && (
                  <div className="flex justify-between text-sm text-green-600">
                    <span>Difference</span>
                    <span>
                      {priceDifference > 0 ? "+" : ""}
                      {priceDifference} {getCurrencySymbol(currency)}
                    </span>
                  </div>
                )}
              </div>
            )}

            <div className="flex items-center space-x-2">
              <Checkbox
                id="email"
                checked={sendEmail}
                onCheckedChange={(checked) => setSendEmail(checked as boolean)}
              />
              <label htmlFor="email" className="text-sm text-muted-foreground">
                Send an email to the client with this update
              </label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddAddOn} disabled={isLoading}>
              {isLoading ? "Adding..." : "Add Add-on"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Add-on Modal */}
      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Add-on</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Add-on</Label>
              <div className="p-2 bg-gray-50 rounded">
                {editingAddOn?.add_ons.add_ons_info?.[0]?.name ||
                  "Unnamed Add-on"}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-quantity">Number of Units</Label>
              <Input
                id="edit-quantity"
                type="number"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                min="1"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-price">Price Override (per unit)</Label>
              <Input
                id="edit-price"
                type="number"
                value={priceOverride}
                onChange={(e) => setPriceOverride(e.target.value)}
              />
            </div>

            <div className="bg-gray-50 p-4 rounded-lg space-y-2">
              <div className="flex justify-between text-sm">
                <span>Total Price</span>
                <span>
                  {(
                    parseFloat(priceOverride || "0") * parseInt(quantity || "0")
                  ).toFixed(2)}{" "}
                  {getCurrencySymbol(currency)}
                </span>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdateAddOn} disabled={isLoading}>
              {isLoading ? "Updating..." : "Update Add-on"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={isDeleteModalOpen} onOpenChange={setIsDeleteModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove Add-on</DialogTitle>
            <DialogDescription>
              Are you sure you want to remove this add-on from the booking?
            </DialogDescription>
          </DialogHeader>

          {deletingAddOn && (
            <div className="py-4">
              <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                <div className="font-medium">
                  {deletingAddOn.add_ons.add_ons_info?.[0]?.name ||
                    "Unnamed Add-on"}
                </div>
                <div className="text-sm text-muted-foreground">
                  Quantity: {deletingAddOn.quantity} × {deletingAddOn.amount}{" "}
                  {getCurrencySymbol(currency)} = {deletingAddOn.total_price} {getCurrencySymbol(currency)}
                </div>
              </div>
              <p className="mt-4 text-sm text-muted-foreground">
                This action will archive the add-on. It can be restored if
                needed.
              </p>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsDeleteModalOpen(false);
                setDeletingAddOn(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={confirmRemoveAddOn}
              disabled={isLoading}
            >
              {isLoading ? "Removing..." : "Remove Add-on"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
